import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Paper,
  IconButton,
  Divider,
  Alert,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getSiteContent, updateSiteContent } from '../../services/storage';

interface Stat {
  value: string;
  label: string;
}

interface Value {
  title: string;
  desc: string;
  image: string;
}

interface TeamMember {
  name: string;
  role: string;
  image: string;
  desc: string;
}

interface AboutContent {
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  intro_label: string;
  intro_title: string;
  intro_text: string;
  intro_image: string;
  stats: Stat[];
  values: Value[];
  team: TeamMember[];
  cta_title: string;
  cta_text: string;
}

const EMPTY_CONTENT: AboutContent = {
  hero_title: '',
  hero_subtitle: '',
  hero_image: '',
  intro_label: '',
  intro_title: '',
  intro_text: '',
  intro_image: '',
  stats: [
    { value: '', label: '' },
    { value: '', label: '' },
    { value: '', label: '' },
    { value: '', label: '' },
  ],
  values: [
    { title: '', desc: '', image: '' },
    { title: '', desc: '', image: '' },
    { title: '', desc: '', image: '' },
    { title: '', desc: '', image: '' },
  ],
  team: [],
  cta_title: '',
  cta_text: '',
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
      {value === index && children}
    </Box>
  );
}

const AdminUeberUns: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [content, setContent] = useState<AboutContent>(EMPTY_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getSiteContent('about_us')
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          const loaded = data as unknown as AboutContent;
          setContent({
            ...EMPTY_CONTENT,
            ...loaded,
            stats: Array.isArray(loaded.stats) && loaded.stats.length > 0 ? loaded.stats : EMPTY_CONTENT.stats,
            values: Array.isArray(loaded.values) && loaded.values.length > 0 ? loaded.values : EMPTY_CONTENT.values,
            team: Array.isArray(loaded.team) ? loaded.team : [],
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    setError('');
    try {
      await updateSiteContent('about_us', content as unknown as Record<string, unknown>);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch {
      setError('Fehler beim Speichern. Bitte versuchen Sie es erneut.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof AboutContent, value: string) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const updateStat = (index: number, key: keyof Stat, value: string) => {
    setContent((prev) => {
      const stats = [...prev.stats];
      stats[index] = { ...stats[index], [key]: value };
      return { ...prev, stats };
    });
  };

  const updateValue = (index: number, key: keyof Value, value: string) => {
    setContent((prev) => {
      const values = [...prev.values];
      values[index] = { ...values[index], [key]: value };
      return { ...prev, values };
    });
  };

  const updateTeamMember = (index: number, key: keyof TeamMember, value: string) => {
    setContent((prev) => {
      const team = [...prev.team];
      team[index] = { ...team[index], [key]: value };
      return { ...prev, team };
    });
  };

  const addTeamMember = () => {
    setContent((prev) => ({
      ...prev,
      team: [...prev.team, { name: '', role: '', image: '', desc: '' }],
    }));
  };

  const removeTeamMember = (index: number) => {
    setContent((prev) => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Laden…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          Über uns – Inhaltsverwaltung
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Inhalt erfolgreich gespeichert.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 0, border: '1px solid #e8e8e8', boxShadow: 'none' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #e8e8e8', px: 2 }}
        >
          <Tab label="Hero & Intro" />
          <Tab label="Statistiken" />
          <Tab label="Werte" />
          <Tab label="Team" />
        </Tabs>

        {/* Tab 1: Hero & Intro */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Hero-Bereich
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Hero-Titel"
                  fullWidth
                  value={content.hero_title}
                  onChange={(e) => updateField('hero_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Hero-Untertitel"
                  fullWidth
                  value={content.hero_subtitle}
                  onChange={(e) => updateField('hero_subtitle', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Hero-Bild URL"
                  fullWidth
                  value={content.hero_image}
                  onChange={(e) => updateField('hero_image', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} mb={2}>
              Intro-Bereich
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Intro-Label (Overline)"
                  fullWidth
                  value={content.intro_label}
                  onChange={(e) => updateField('intro_label', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Intro-Titel"
                  fullWidth
                  value={content.intro_title}
                  onChange={(e) => updateField('intro_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Intro-Text (Absätze mit Leerzeile trennen)"
                  fullWidth
                  multiline
                  rows={5}
                  value={content.intro_text}
                  onChange={(e) => updateField('intro_text', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Intro-Bild URL"
                  fullWidth
                  value={content.intro_image}
                  onChange={(e) => updateField('intro_image', e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} mb={2}>
              CTA-Banner
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CTA-Titel"
                  fullWidth
                  value={content.cta_title}
                  onChange={(e) => updateField('cta_title', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="CTA-Text"
                  fullWidth
                  value={content.cta_text}
                  onChange={(e) => updateField('cta_text', e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 2: Statistiken */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Statistiken (4 Kennzahlen)
            </Typography>
            <Grid container spacing={3}>
              {content.stats.map((stat, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Statistik {i + 1}
                    </Typography>
                    <TextField
                      label="Wert (z.B. 10+, 100%)"
                      fullWidth
                      size="small"
                      value={stat.value}
                      onChange={(e) => updateStat(i, 'value', e.target.value)}
                    />
                    <TextField
                      label="Beschriftung"
                      fullWidth
                      size="small"
                      value={stat.label}
                      onChange={(e) => updateStat(i, 'label', e.target.value)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 3: Werte */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Unsere Werte (4 Karten)
            </Typography>
            <Grid container spacing={3}>
              {content.values.map((v, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      Wert {i + 1}
                    </Typography>
                    <TextField
                      label="Titel"
                      fullWidth
                      size="small"
                      value={v.title}
                      onChange={(e) => updateValue(i, 'title', e.target.value)}
                    />
                    <TextField
                      label="Beschreibung"
                      fullWidth
                      size="small"
                      multiline
                      rows={3}
                      value={v.desc}
                      onChange={(e) => updateValue(i, 'desc', e.target.value)}
                    />
                    <TextField
                      label="Bild URL"
                      fullWidth
                      size="small"
                      value={v.image}
                      onChange={(e) => updateValue(i, 'image', e.target.value)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Tab 4: Team */}
        <TabPanel value={tab} index={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Team-Mitglieder
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addTeamMember}
                size="small"
              >
                Hinzufügen
              </Button>
            </Box>
            {content.team.length === 0 && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Noch keine Team-Mitglieder vorhanden.
              </Typography>
            )}
            <Grid container spacing={3}>
              {content.team.map((member, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, position: 'relative' }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Mitglied {i + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeTeamMember(i)}
                        aria-label="Löschen"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      label="Name"
                      fullWidth
                      size="small"
                      value={member.name}
                      onChange={(e) => updateTeamMember(i, 'name', e.target.value)}
                    />
                    <TextField
                      label="Rolle / Position"
                      fullWidth
                      size="small"
                      value={member.role}
                      onChange={(e) => updateTeamMember(i, 'role', e.target.value)}
                    />
                    <TextField
                      label="Foto URL (leer = Initialen-Avatar)"
                      fullWidth
                      size="small"
                      value={member.image}
                      onChange={(e) => updateTeamMember(i, 'image', e.target.value)}
                    />
                    <TextField
                      label="Kurzbeschreibung"
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={member.desc}
                      onChange={(e) => updateTeamMember(i, 'desc', e.target.value)}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* Save footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #e8e8e8',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ minWidth: 140 }}
          >
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminUeberUns;
